// drain-runner.ts (Story 7.4) — the MAIN-THREAD wiring around the pure `drain-core`. It supplies
// the two things drain-core deliberately does NOT import (so it stays pure): the Dexie
// DB handle (the npm `db` singleton) and the network transport (`apiFetch`).
//
// The main thread is the ONLY drain driver (a Service-Worker transport existed until seam-74
// removed it): it holds the live access token, so its replays authenticate directly.
// `isEffectivelyOnline()` derivation is Story 7.7 — out of scope here.
//
// CORRECTED 2026-09-25: this comment used to hand a broader DRAIN-trigger matrix (visibilitychange,
// cross-tab BroadcastChannel, "Sync now") to Story 7.7. That reading was wrong. What drains today:
//   - this file (`startMainThreadDrainTriggers`): the `online` event; every promotion to a non-null
//     access token (gh-634); one attempt at registration.
//   - `draft-producer-runner.ts`: every draft create, and every save that commits or queues.
//   - `auth-context.tsx`: the gh-461 reconnect promotion — while a session is offline-restored it
//     listens on `online` and `visibilitychange`→visible, and drains once the refresh succeeds. Its
//     offline-restore kick is token-gated to a no-op (the token is always null on that path).
//   - `resolve-strategies.ts`: after a conflict is resolved and after a 410 is adopted.
// Story 7.7's `visibilitychange` listeners (online-status.ts and the IDB-reactive hooks) re-derive
// `isEffectivelyOnline()` and re-read IndexedDB; they do not drain. Cross-tab BroadcastChannel, a
// manual "Sync now" and a Service-Worker drain exist nowhere and are not pending — seam-74's removal
// decision (2026-07-29) records FR26 as served by the main-thread triggers.

import { apiFetch, ApiError, getAccessToken, subscribeAccessToken } from "./api-client";
import {
  drainQueue,
  terminalReplayError,
  type DrainDeps,
  type DrainStats,
  type ReplayResult,
  type ServerState,
} from "./drain-core";
import type { OutboundOp } from "./idb-schema-def";
import { db, getMeta } from "./idb-schema";
import { readOwnerIdentity } from "./owner-scope";

/** The PHI-free response body shape both create and save-draft return (we only consume `version`). */
interface ReplayResponse {
  id: string;
  version: number;
  state: string;
  updated_at?: string;
}

/**
 * Gate a 2xx replay body before drain-core's success branch consumes it. A garbled 2xx (proxy /
 * captive portal / SW cache serving non-JSON) would otherwise write `version: undefined` into the
 * local draft AND delete the op — poisoning every later save with `X-Client-Version: "undefined"`.
 * Malformed → the retryable error result: op retained, draft untouched, next drain retries.
 * Twin of the direct-save guard in `draft-producer-runner.ts` (`saveOnline`) — same predicate.
 */
function checkedSuccess(status: 200 | 201, data: ReplayResponse): ReplayResult {
  if (!Number.isInteger(data?.version) || data.version < 0 || typeof data.state !== "string") {
    return {
      status,
      error: `replay returned a malformed body (version=${String(data?.version)}, state=${String(
        data?.state,
      )})`,
    };
  }
  return { status, data };
}

/**
 * Main-thread transport: route a `create`/`update` op to the network and normalize to a
 * `ReplayResult`. `apiFetch` injects the in-memory Bearer token and handles 401→refresh→retry.
 *
 *  - `create` → `POST /evaluations/create` with the op payload INCLUDING the client-minted `id`
 *    (== eval_id == drafts.id). First sync → 201, idempotent replay → 200; both unwrap to the same
 *    body and drain-core treats them identically (AC9).
 *  - `update` → `POST /evaluations/{eval_id}/save-draft` with `X-Client-Version`.
 *
 * `apiFetch` discards the HTTP status on success, so we report 201 for a create and 200 for an
 * update — drain-core's success branch (AC10) does not distinguish them. On an `ApiError` we map
 * 409/410 to a conflict result (lifting `details.server_state`) and everything else to a retryable
 * error result; api-client already wraps a network failure as `ApiError(NETWORK_ERROR, status 0)`.
 */
export async function mainThreadReplay(op: OutboundOp): Promise<ReplayResult> {
  try {
    if (op.op === "create") {
      const data = await apiFetch<ReplayResponse>("/evaluations/create", {
        method: "POST",
        body: op.payload, // includes `id`
      });
      return checkedSuccess(201, data);
    }
    const data = await apiFetch<ReplayResponse>(
      `/evaluations/${op.eval_id}/save-draft`,
      {
        method: "POST",
        body: op.payload,
        headers: { "X-Client-Version": String(op.client_version) },
      },
    );
    return checkedSuccess(200, data);
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 409 || err.status === 410) {
        const server_state = (err.details as { server_state?: ServerState } | undefined)
          ?.server_state;
        if (server_state) return { status: err.status, server_state };
        // Contract: the only 409 is the version conflict and the only 410 the already_submitted,
        // both of which carry `server_state`. A 409/410 WITHOUT it falls through to the retryable
        // path and would be retried on every drain indefinitely — surface it for monitoring rather
        // than burning retries silently (the assumption the API never does this is now observable).
        console.warn(
          `[drain] ${err.status} without server_state (op ${op.op} ${op.eval_id}) — treating as retryable`,
        );
      }
      // Story 8.1 — terminal gate refusals (403 CALIB-001) are classified by the
      // SHARED helper so no transport-local copy of the retry policy can drift;
      // drain-core dequeues them instead of retrying forever.
      const terminal = terminalReplayError(err.status, err.code, err.message);
      if (terminal) return terminal;
      return { status: err.status, error: err.message };
    }
    // A non-ApiError throw is unexpected; let drain-core's catch treat it as retryable.
    throw err;
  }
}

function buildDeps(): DrainDeps {
  return {
    db,
    replay: mainThreadReplay,
    getForceOffline: async () => (await getMeta<boolean>("force_offline")) === true,
    // Story 19.6 Unit E — the drain replays ONLY the current device owner's ops. On a shared device
    // an un-drained op belonging to the previous clinician would otherwise be submitted under THIS
    // session's token, pushing their PHI to the server as this clinician's work. Unowned ops stay
    // queued, untouched, for whoever owns them.
    owner: readOwnerIdentity,
    now: () => new Date().toISOString(),
    // A Dexie rw transaction over the two tables drain-core touches, so each op's dequeue + draft
    // reconciliation is atomic (the network replay already happened, outside this).
    runAtomic: (fn) => db.transaction("rw", db.outbound_queue, db.drafts, fn),
  };
}

/**
 * Window event fired after every main-thread drain settles (Story 7.5 AC8). The conflict reader
 * hook (`useEvaluationConflict`) subscribes to it so a `drafts.conflict` a drain just PINNED (or a
 * resolution just CLEARED) is reflected in the UI without polling — drain-core has no React
 * coupling, so this is the caller-side notification AC8 option (b) calls for. `detail` is the
 * `DrainStats` of the completed pass.
 */
export const DRAIN_COMPLETE_EVENT = "franklin:drain-complete";

// In-flight guard (AC15): coalesce overlapping triggers (load + `online` firing together) onto one
// drain so two passes never race over the same queue.
let inFlight: Promise<DrainStats> | null = null;

/**
 * Drain once, guarded twice:
 *  - a concurrent call returns the in-flight drain rather than starting a second (AC15);
 *  - gh-634 — WITHOUT an access token no pass starts at all: zeroed stats, no network, no
 *    `DRAIN_COMPLETE`. The gate is falsy, not null — apiFetch builds the Authorization header
 *    truthily, so an empty-string token would also go out bearer-less. A replay can never leave
 *    the app without a bearer — the backend answers a
 *    credential-less request with a generic 403 "Not authenticated" (code null), which neither the
 *    401→refresh→retry path nor `terminalReplayError` recognises, so an ungated pass just burned an
 *    attempt and retained the op. The token-promotion trigger below drains the moment auth lands.
 */
export function triggerMainThreadDrain(): Promise<DrainStats> {
  if (inFlight) return inFlight;
  if (!getAccessToken()) {
    return Promise.resolve({ queued: 0, succeeded: 0, conflicts: 0, adopted: 0, failed: 0 });
  }
  inFlight = drainQueue(buildDeps())
    .then((stats) => {
      // Notify the conflict reader hook (Story 7.5) once the queue settles. Guarded for a
      // non-DOM realm (this runner is main-thread only, but the guard keeps it import-safe).
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(DRAIN_COMPLETE_EVENT, { detail: stats }));
      }
      return stats;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

let started = false;

/**
 * Wire the minimal main-thread triggers: `online`, access-token promotion, and an initial attempt.
 * Idempotent (registers once); errors are swallowed — a failed drain simply retries on the next
 * trigger.
 *
 * Token promotion is a trigger (gh-634): on a reload of an already-signed-in session — the normal
 * PWA reopen — the queue is NOT empty at load, but the memory-only access token does not exist
 * until the boot silent-refresh resolves, so an eager app-load drain replayed unauthenticated and
 * nothing re-fired after auth landed. Draining on every promotion to a non-null token covers boot
 * refresh, login after expiry, and the gh-461 reconnect promotion alike; a rotation while a pass is
 * in flight coalesces via the guard, and an idle rotation is a cheap empty-queue no-op. The initial
 * attempt stays for the one case the subscription cannot see: a token already live at registration.
 */
export function startMainThreadDrainTriggers(): void {
  if (started) return;
  started = true;
  window.addEventListener("online", () => {
    void triggerMainThreadDrain().catch(() => {});
  });
  subscribeAccessToken((token) => {
    if (token) void triggerMainThreadDrain().catch(() => {});
  });
  void triggerMainThreadDrain().catch(() => {});
}
